import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { PensionArrangementType } from "../../../../../../../Wrap/Constants/Enums/Domain/PensionArrangementType";
import { TransferType } from "../../../../../../../Wrap/Constants/Enums/Domain/TransferType";
import { Money } from "../../../../../../../Wrap/Core/DataTypes/Money";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Table } from "../../../../../../Shared/Table/Table";
import { PensionHelper } from "../../Helpers/PensionHelper";
import { SectionHeader } from "../../SharedComponents";
import { ChooseAssetsToSellAssets } from "./Store/DrawdownCalculationStoreStatic";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";

interface ICrystallizationPerContributionProps extends IProcessComponentProps {
    data: DrawdownModel;
    modelData: PensionBase;
    process? : ProcessType;
}

interface ICrystallizationPerContributionState {}

interface ICrystallisationPerConributionTableRow {
    Contribution?: string;
    CrystallisationAmount?: Money;
    LSA?: Money;
    LSDBA?: Money;
    TaxFreeLumpSum?: Money;
}

export class CrystallizationPerContribution extends ProcessComponent<
    ICrystallizationPerContributionProps,
    ICrystallizationPerContributionState
> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    private getLSAorLSDBACalculation(payment: number, totalAmount: number, allowanceUsedTotal: number): Money {
        payment = payment || 0;
        const value = (payment / totalAmount) * allowanceUsedTotal;
        return {
            Amount: Math.round(Math.pow(10, 2) * value) / Math.pow(10, 2),
            Currency: CurrencyCode.GBP
        }
    }

    private getPCLSAmount(): number {
        return this.props?.data?.CurrentPclsEntitlement?.Amount
    }

    private getTableData = () => {
        const { data, modelData } = this.props;
        const availableLTA =
            data?.LifetimeAllowance && data?.LifetimeAllowance?.AvailablePersonalAllowance
                ? data?.LifetimeAllowance?.AvailablePersonalAllowance?.Amount || 0
                : 0;
        const totalValue =
            PensionHelper.singlePaymentsTotal(modelData) +
            PensionHelper.getTransfersTotal(
                (modelData?.PensionTransfers || []).filter(
                    (ele) =>
                        ele.PensionSource == PensionArrangementType.Accumulation &&
                        ele.TransferType == TransferType.Cash
                )
            );
        const contributionRow: ICrystallisationPerConributionTableRow[] = [];
        if (modelData?.ClientSinglePayment?.Money?.Amount > 0) {
            const value = PensionHelper.clientSinglePayment(modelData);
            contributionRow.push({
                Contribution: "Individual contribution",
                CrystallisationAmount: value,
                LSA: this.getLSAorLSDBACalculation(value.Amount, totalValue, data?.DrawdownCalculations?.LumpSumAllowance?.UsedByCrystallisation?.Amount ?? 0),
                LSDBA: this.getLSAorLSDBACalculation(value.Amount, totalValue, data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedByCrystallisation?.Amount ?? 0),
                TaxFreeLumpSum: {
                    Amount: this.getPCLSAmount(),
                    Currency: CurrencyCode.GBP,
                },
            });
        }

        if (modelData?.EmployerSinglePayment?.Money?.Amount > 0) {
            const value = PensionHelper.employerSinglePayment(modelData);
            contributionRow.push({
                Contribution: "Employer contribution",
                CrystallisationAmount: value,
                LSA: this.getLSAorLSDBACalculation(value.Amount, totalValue, data?.DrawdownCalculations?.LumpSumAllowance?.UsedByCrystallisation?.Amount ?? 0),
                LSDBA: this.getLSAorLSDBACalculation(value.Amount, totalValue, data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedByCrystallisation?.Amount ?? 0),
                TaxFreeLumpSum: {
                    Amount: this.getPCLSAmount(),
                    Currency: CurrencyCode.GBP,
                },
            });
        }

        if (modelData?.ThirdPartySinglePayment?.Money?.Amount > 0) {
            const value = PensionHelper.thirdPartySinglePayment(modelData);
            contributionRow.push({
                Contribution: "Third party contribution",
                CrystallisationAmount: value,
                LSA: this.getLSAorLSDBACalculation(value.Amount, totalValue, data?.DrawdownCalculations?.LumpSumAllowance?.UsedByCrystallisation?.Amount ?? 0),
                LSDBA: this.getLSAorLSDBACalculation(value.Amount, totalValue, data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedByCrystallisation?.Amount ?? 0),
                TaxFreeLumpSum: {
                    Amount: this.getPCLSAmount(),
                    Currency: CurrencyCode.GBP,
                },
            });
        }

        if (modelData?.EmployerSalaryDeductionSinglePayment?.Money?.Amount > 0) {
            const value = PensionHelper.employerSalaryDeductionSinglePayment(modelData);
            contributionRow.push({
                Contribution: "Employer salary deduction contribution",
                CrystallisationAmount: value,
                LSA: this.getLSAorLSDBACalculation(value.Amount, totalValue, data?.DrawdownCalculations?.LumpSumAllowance?.UsedByCrystallisation?.Amount ?? 0),
                LSDBA: this.getLSAorLSDBACalculation(value.Amount, totalValue, data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedByCrystallisation?.Amount ?? 0),
                TaxFreeLumpSum: {
                    Amount: this.getPCLSAmount(),
                    Currency: CurrencyCode.GBP,
                },
            });
        }

        if (modelData?.PensionTransfers && modelData.PensionTransfers.length > 0) {
            modelData.PensionTransfers.filter(
                (ele) =>
                    ele.PensionSource == PensionArrangementType.Accumulation && ele.TransferType == TransferType.Cash
            ).forEach((transfer, index) => {
                const value = transfer.CurrentValue;
                contributionRow.push({
                    Contribution: `Transfer ${index + 1}`,
                    CrystallisationAmount: value,
                    LSA: this.getLSAorLSDBACalculation(value.Amount, totalValue, data?.DrawdownCalculations?.LumpSumAllowance?.UsedByCrystallisation?.Amount ?? 0),
                    LSDBA: this.getLSAorLSDBACalculation(value.Amount, totalValue, data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedByCrystallisation?.Amount ?? 0),
                    TaxFreeLumpSum: {
                        Amount: this.getPCLSAmount(),
                        Currency: CurrencyCode.GBP,
                    },
                });
            });
        }

        contributionRow.sort((a, b) => {
            if (a.CrystallisationAmount && b.CrystallisationAmount) {
                return b.CrystallisationAmount.Amount - a.CrystallisationAmount.Amount;
            }
            return 0;
        });

        return contributionRow;
    };

    public render(): JSX.Element {
        const { data, modelData } = this.props;
        const tableData = this.getTableData();
        const totalCrystallisedContribution = tableData.reduce((x, y) => x + y.CrystallisationAmount.Amount, 0);
        const totalContributionTaxFreeLumpSum = tableData.reduce((x, y) => x + y.TaxFreeLumpSum.Amount, 0);
        const totalTaxFreeLumpSum =
            this.props.process != ProcessType.TopUpWithITFLS && totalContributionTaxFreeLumpSum > data?.CurrentPclsEntitlement.Amount
                ? data?.CrystallisationDetails?.CrystallisationAmount?.Amount * 0.25
                : totalContributionTaxFreeLumpSum;

        return (
            <>
                {SectionHeader("Drawdown per contribution")}
                <p className="text-field-info">The values here may change based on the contribution amounts we receive</p>
                <Table
                    data={tableData}
                    col={[
                        {
                            title: "Contribution",
                            key: "Contribution",
                        },
                        {
                            title: "Drawdown amount",
                            key: "CrystallisationAmount",
                            render: (data, row, index) => {
                                return MoneyDisplayHelper.getMoneyFormat(data);
                            },
                        },
                        {
                            title: "LSA used",
                            key: "LSA",
                            render: (data, row: ChooseAssetsToSellAssets, index) => {
                                return MoneyDisplayHelper.getMoneyFormat(data);
                            },
                        },
                        {
                            title: "LSDBA used",
                            key: "LSA",
                            render: (data, row: ChooseAssetsToSellAssets, index) => {
                                return MoneyDisplayHelper.getMoneyFormat(data);
                            },
                        },
                        {
                            title: "Tax-free lump sum",
                            key: "TaxFreeLumpSum",
                            render: (data, row: ChooseAssetsToSellAssets, index) => {
                                return MoneyDisplayHelper.getMoneyFormat(data);
                            },
                        },
                    ]}
                    TotalBody={[
                        { data: "Total" },
                        {
                            data: (
                                <span>
                                    {MoneyDisplayHelper.getMoneyFormat({
                                        Amount: Number(
                                            totalCrystallisedContribution.toFixed(2)
                                        ),
                                        Currency: CurrencyCode.GBP,
                                    })}
                                </span>
                            ),
                        },
                        {
                            data: (
                                <span>
                                    {MoneyDisplayHelper.getMoneyFormat({ Amount: data?.DrawdownCalculations?.LumpSumAllowance?.UsedByCrystallisation?.Amount ?? 0, Currency: CurrencyCode.GBP })}
                                </span>
                            )
                        },
                        {
                            data: (
                                <span>
                                    {MoneyDisplayHelper.getMoneyFormat({ Amount: data?.DrawdownCalculations?.LumpSumDeathBenefitAllowance?.UsedByCrystallisation?.Amount ?? 0, Currency: CurrencyCode.GBP })}
                                </span>
                            )
                        },
                        {
                            data: (
                                <span>
                                    {MoneyDisplayHelper.getMoneyFormat({ Amount: totalTaxFreeLumpSum, Currency: CurrencyCode.GBP })}
                                </span>
                            )
                        },
                    ]}
                />
            </>
        );
    }
}
